import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import {
  ACCESS_POLICY_SEED,
  API_KEYS_SEED,
  DATA_IMPORT_SEED,
  EMAIL_SENDER_IDENTITY_SEED,
  INVOICES_SEED,
  ORGANIZATION_DEFAULTS_SEED,
  ORGANIZATION_PROFILES_SEED,
  RETENTION_POLICY_SEED,
  SENDING_DOMAINS_SEED,
  WEBHOOKS_SEED,
} from '@common/data';
import type {
  ApiKey,
  DataImportConfig,
  EmailSenderIdentity,
  Invoice,
  OrganizationAccessPolicy,
  OrganizationDefaults,
  OrganizationProfile,
  RetentionPolicy,
  SendingDomain,
  WebhookSubscription,
} from '@common/types';

import { OrganizationService } from './organization.service';

const SAVE_LATENCY_MS = 320;
const VERIFY_LATENCY_MS = 900;

/**
 * Everything the organisation itself owns. One service rather than nine, because these settings are
 * read together (the shell needs profile + defaults + policy to render its nav states) and a split
 * would only add barrels.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationSettingsService {
  private readonly organizations = inject(OrganizationService);

  private readonly profiles = signal<Record<string, OrganizationProfile>>({
    ...ORGANIZATION_PROFILES_SEED,
  });
  private readonly defaults = signal<Record<string, OrganizationDefaults>>({
    ...ORGANIZATION_DEFAULTS_SEED,
  });
  private readonly accessPolicy = signal<OrganizationAccessPolicy>(ACCESS_POLICY_SEED);
  private readonly senderIdentity = signal<EmailSenderIdentity>(EMAIL_SENDER_IDENTITY_SEED);
  private readonly domains = signal<ReadonlyArray<SendingDomain>>(SENDING_DOMAINS_SEED);
  private readonly retention = signal<RetentionPolicy>(RETENTION_POLICY_SEED);
  private readonly keys = signal<ReadonlyArray<ApiKey>>(API_KEYS_SEED);
  private readonly hooks = signal<ReadonlyArray<WebhookSubscription>>(WEBHOOKS_SEED);
  private readonly dataImport = signal<DataImportConfig>(DATA_IMPORT_SEED);
  private readonly invoiceList = signal<ReadonlyArray<Invoice>>(INVOICES_SEED);

  readonly sendingDomains = this.domains.asReadonly();
  readonly apiKeys = this.keys.asReadonly();
  readonly webhooks = this.hooks.asReadonly();
  readonly importConfig = this.dataImport.asReadonly();
  readonly invoices = this.invoiceList.asReadonly();
  readonly policy = this.accessPolicy.asReadonly();
  readonly emailIdentity = this.senderIdentity.asReadonly();
  readonly retentionPolicy = this.retention.asReadonly();

  readonly activeOrganizationName = computed<string>(
    () => this.organizations.active()?.name ?? 'the organisation',
  );

  profileFor(orgId: string | null): OrganizationProfile {
    const existing = orgId ? this.profiles()[orgId] : undefined;
    if (existing) return existing;
    const org = this.organizations.byId(orgId);
    return {
      organizationId: orgId ?? '',
      legalName: org?.name ?? '',
      tradingName: org?.name ?? '',
      logoUrl: '',
      registrationNumber: '',
      taxNumber: '',
      websiteUrl: '',
      address: {
        line1: '',
        line2: '',
        city: org?.location.split(',')[0]?.trim() ?? '',
        region: '',
        postalCode: '',
        country: org?.location.split(',')[1]?.trim() ?? '',
      },
      primaryContact: { name: '', email: '', phone: '' },
      supportContact: { name: '', email: '', phone: '' },
    };
  }

  defaultsFor(orgId: string | null): OrganizationDefaults {
    const existing = orgId ? this.defaults()[orgId] : undefined;
    if (existing) return existing;
    return { organizationId: orgId ?? '', lockedKeys: [] };
  }

  isLocked(orgId: string | null, key: string): boolean {
    return this.defaultsFor(orgId).lockedKeys.includes(key);
  }

  saveProfile(profile: OrganizationProfile): Observable<OrganizationProfile> {
    this.profiles.update((map) => ({ ...map, [profile.organizationId]: profile }));
    return of(profile).pipe(delay(SAVE_LATENCY_MS));
  }

  saveDefaults(next: OrganizationDefaults): Observable<OrganizationDefaults> {
    this.defaults.update((map) => ({ ...map, [next.organizationId]: next }));
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  toggleLock(orgId: string, key: string): Observable<OrganizationDefaults> {
    const current = this.defaultsFor(orgId);
    const locked = current.lockedKeys.includes(key)
      ? current.lockedKeys.filter((entry) => entry !== key)
      : [...current.lockedKeys, key];
    return this.saveDefaults({ ...current, lockedKeys: locked });
  }

  saveAccessPolicy(next: OrganizationAccessPolicy): Observable<OrganizationAccessPolicy> {
    this.accessPolicy.set(next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  saveEmailIdentity(next: EmailSenderIdentity): Observable<EmailSenderIdentity> {
    this.senderIdentity.set(next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  verifyDomain(id: string): Observable<SendingDomain | undefined> {
    this.domains.update((list) =>
      list.map((domain) =>
        domain.id === id
          ? {
              ...domain,
              status: 'verified',
              lastCheckedIso: new Date().toISOString(),
              records: domain.records.map((record) => ({ ...record, verified: true })),
            }
          : domain,
      ),
    );
    return of(this.domains().find((domain) => domain.id === id)).pipe(delay(VERIFY_LATENCY_MS));
  }

  saveRetention(next: RetentionPolicy): Observable<RetentionPolicy> {
    this.retention.set(next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }

  revokeKey(id: string): Observable<void> {
    this.keys.update((list) =>
      list.map((key) =>
        key.id === id ? { ...key, revoked: true, lastUsedIso: key.lastUsedIso } : key,
      ),
    );
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  createKey(label: string, scopes: ReadonlyArray<string>): Observable<ApiKey> {
    const key: ApiKey = {
      id: `key-${Date.now().toString(36)}`,
      label,
      prefix: `gr_live_${Math.random().toString(16).slice(2, 6)}`,
      scopes,
      createdBy: 'You',
      createdAtIso: new Date().toISOString(),
      lastUsedIso: null,
      revoked: false,
    };
    this.keys.update((list) => [key, ...list]);
    return of(key).pipe(delay(SAVE_LATENCY_MS));
  }

  toggleWebhook(id: string): Observable<void> {
    this.hooks.update((list) =>
      list.map((hook) => (hook.id === id ? { ...hook, isEnabled: !hook.isEnabled } : hook)),
    );
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  deleteWebhook(id: string): Observable<void> {
    this.hooks.update((list) => list.filter((hook) => hook.id !== id));
    return of(undefined).pipe(delay(SAVE_LATENCY_MS));
  }

  saveImportConfig(next: DataImportConfig): Observable<DataImportConfig> {
    this.dataImport.set(next);
    return of(next).pipe(delay(SAVE_LATENCY_MS));
  }
}
